import { MatchingAPI } from '@adornis/accounting/entry-matching/api/MatchingAPI.js';
import { MatchingReview } from '@adornis/accounting/entry-matching/client/MatchingReview.js';
import { OpenItemsAging } from '@adornis/accounting/opos/client/OpenItemsAging.js';
import { stateful, useEffect } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { addPageHeaderAdditional, PageHeader, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { PageTabs } from '@martinus/styles/client/PageTabs.js';
import { html, nothing } from 'lit';
import { AdminDashboardActions } from '../../admin-dashboard/AdminDashboardPolicy.js';
import { TestBWA } from '../../client/temp-bwa.js';
import { MartinusTemporaryActions } from '../../MartinusTemporaryPolicy.js';
import { NoPermission } from '../../permissions/client/NoPermission.js';
import { useMartinusABAC } from '../../permissions/client/useMartinusAbac.js';
import { MartinusAccountingEntry } from '../../quittungen/entities/MartinusAccountingEntry.js';
import { DebitorAccountLabel } from './SgAccountingDetails.js';
import { useAccountingMandanten } from './useAccountingMandanten.js';

enum AccountingTab {
  OVERVIEW = 'Übersicht',
  BWA = 'BWA',
  MATCHING = 'Zuordnung',
}

const accountingSubpages = {
  overview: { label: AccountingTab.OVERVIEW, path: '/' },
  bwa: { label: AccountingTab.BWA, path: '/bwa' },
  matching: { label: AccountingTab.MATCHING, path: '/zuordnung' },
} as const;

/** the dashboard: how old what is open is, and what waits in the matching review */
const AccountingOverview = stateful(() => {
  const { loading, schoolIDs } = useAccountingMandanten();
  // the aging is admin only; getOpenItemsAging is guarded the same way on the server
  const { can: canViewAging } = useMartinusABAC({ action: AdminDashboardActions.VIEW_ADMIN_DASHBOARD });
  // the overall count, no filters: the card only says whether the Zuordnung needs a visit
  const pending = useQuery(
    () =>
      loading
        ? Promise.resolve(null)
        : MatchingAPI.getMatchingOverview(schoolIDs, '', [])({ _id: 1, openCount: 1, openAccountCount: 1 }),
    [loading, schoolIDs.join(',')],
  ).value;
  if (loading) return PageLoader({});

  return html`
    <div class="grid items-start gap-4 ${canViewAging ? 'lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'lg:max-w-md'}">
      ${canViewAging ? OpenItemsAging({ perspectiveIDs: schoolIDs }) : nothing}
      <div class="card">
        <div class="card-body gap-2 p-4">
          <h2 class="font-semibold md:text-lg">Zuordnung</h2>
          <p class="text-2xl font-semibold tabular-nums">${pending?.openCount ?? '–'}</p>
          <p class="text-sm text-base-content/70">
            Vorschläge zu prüfen auf ${pending?.openAccountCount ?? 0} Konten — Buchungen, die das System nicht sicher
            zuordnen konnte.
          </p>
          <div class="card-actions justify-end">
            <button type="button" class="btn btn-sm" @click=${() => accountingRouter.goTo(AccountingTab.MATCHING)}>
              Zur Zuordnung
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
});

/** the matching review over the schools the user may see */
const AccountingMatching = stateful(() => {
  const { loading, mandanten, schoolIDs } = useAccountingMandanten();
  if (loading) return PageLoader({});
  return MatchingReview({
    perspectiveIDs: schoolIDs,
    entryEntityClass: MartinusAccountingEntry,
    renderAccountLabel: (account, mandant) => DebitorAccountLabel({ account, mandant }),
    renderMandant: mandant => mandanten.find(m => m.accountingNumber === mandant)?.name ?? mandant,
  });
});

export const accountingRouter = makeRouter({
  [accountingSubpages.overview.label]: { path: accountingSubpages.overview.path, render: () => AccountingOverview() },
  [accountingSubpages.bwa.label]: { path: accountingSubpages.bwa.path, render: () => TestBWA() },
  [accountingSubpages.matching.label]: { path: accountingSubpages.matching.path, render: () => AccountingMatching() },
});

export const AccountingPage = stateful(() => {
  const { can, loading } = useMartinusABAC({ action: MartinusTemporaryActions.VIEW_ACCOUNTING });

  useEffect(() => {
    addPageHeaderAdditional(
      0,
      PageTabs({
        tabs: Object.values(accountingSubpages).map(subPage => ({ key: subPage.label, label: subPage.label })),
        activeKey: () => accountingRouter.currentRoute.value.route.name,
        onSelect: tab => accountingRouter.goTo(tab),
      }),
    );
    return () => removePageHeaderAdditional(0);
  }, []);

  // a permission refetch keeps the last answer, so the open tab stays mounted meanwhile
  if (!can)
    return loading
      ? PageLoader({})
      : NoPermission({ message: 'Du hast keine Berechtigung, die Buchhaltung zu sehen.' });

  return html`
    ${PageHeader({ title: 'Buchhaltung', onTitleClick: () => accountingRouter.goTo(AccountingTab.OVERVIEW) })}
    <div class="flex min-h-0 flex-1 flex-col p-4">${accountingRouter.outlet.value}</div>
  `;
});
